import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BasicInput from "../../components/BasicInput/BasicInput.jsx";
import PreviousPageHeader from "../../components/PreviousPageHeader/PreviousPageHeader.jsx";
import EmptyListState from "../../components/EmptyListState/EmptyListState.jsx";
import FullWidthAsyncButton from "../../components/FullWidthButton/FullWidthAsyncButton";
import ExerciseTemplateCard from "../../components/ExerciseTemplateCard/ExerciseTemplateCard.jsx";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../fire.js";
import axiosClient from "../../axiosClient";
import DeleteModal from "../../components/DeleteModal/DeleteModal.jsx";
import NotesInput from "../../components/NotesInput/NotesInput.jsx";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";
import MessageModal from "../../components/MessageModal/MessageModal.jsx";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";

const WorkoutTemplate: React.FC = () => {
  const { programId, workoutTemplateId } = useParams() as {
    programId: string;
    workoutTemplateId: string;
  };
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [workoutTemplate, setWorkoutTemplate] =
    useState<WorkoutTemplate | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [exerciseTemplateToDelete, setExerciseTemplateToDelete] = useState<
    string | null
  >(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const blurbRef = React.useRef<HTMLParagraphElement>(null);
  const { innerHeight: screenHeight } = window;

  const LOGO_HEADER = 63;
  const BLURB = blurbRef.current?.clientHeight || 40;
  const WORKOUT_NAME_INPUT = 70;
  const EXERCISES_LIST_TITLE = 40;
  const NEW_EXERCISE_BTN = 62;
  const NOTES_INPUT = 138;
  const MAIN_NAV = 63;

  const OTHER_CONTENT_HEIGHT =
    LOGO_HEADER +
    BLURB +
    WORKOUT_NAME_INPUT +
    EXERCISES_LIST_TITLE +
    NEW_EXERCISE_BTN +
    NOTES_INPUT +
    MAIN_NAV;

  const amountOfCardSkeletons = useMemo(() => {
    const listHeight = screenHeight - OTHER_CONTENT_HEIGHT;
    return Math.floor(listHeight / 58) - 1;
  }, [OTHER_CONTENT_HEIGHT, screenHeight]);

  const getWorkoutTemplate = useCallback(async () => {
    try {
      const getWorkoutTemplateResponse = await axiosClient.get(
        `/api/workout-templates/${workoutTemplateId}`,
      );

      const workoutTemplate: WorkoutTemplate = getWorkoutTemplateResponse.data;

      setWorkoutTemplate(workoutTemplate);
      setIsInitialLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Fetching Workout Template",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(false);
    }
  }, [workoutTemplateId]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    getWorkoutTemplate();
  }, [user, loading, navigate, getWorkoutTemplate]);

  const handleTemplateNameSubmit = async (name: string) => {
    try {
      const updateWorkoutTemplateNameResponse = await axiosClient.put(
        `/api/workout-templates/${workoutTemplateId}`,
        { name: name || "Untitled Program Workout" },
      );

      if (updateWorkoutTemplateNameResponse.status !== 200) {
        throw new Error(updateWorkoutTemplateNameResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Saving Program Workout Title",
        message: `${errorMessage} Please try again or contact support.`,
      });
    }
  };

  const handleAddNewExercise = async () => {
    if (!workoutTemplate) return;

    const newWorkoutTemplateExercises: (
      | ExerciseTemplate
      | DraftExerciseTemplate
    )[] = [...workoutTemplate.exercises];

    const newDraftExerciseTemplate: DraftExerciseTemplate = {
      name: "",
      notes: "",
    };

    newWorkoutTemplateExercises.push(newDraftExerciseTemplate);

    try {
      const addExerciseResponse = await axiosClient.put(
        `/api/workout-templates/${workoutTemplateId}`,
        { exercises: newWorkoutTemplateExercises },
      );

      if (addExerciseResponse.status !== 200) {
        throw new Error(addExerciseResponse.data.message);
      }

      const updatedWorkoutTemplate: WorkoutTemplate = addExerciseResponse.data;

      const newExercise =
        updatedWorkoutTemplate.exercises[
          updatedWorkoutTemplate.exercises.length - 1
        ];

      navigate(
        `/programs/${programId}/workout-templates/${workoutTemplateId}/exercise-templates/${newExercise._id}`,
      );
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Creating Exercise Sequence",
        message: `${errorMessage} Please try again or contact support.`,
      });
    }
  };

  const handleInitiateDeleteExerciseTemplate = (exerciseTemplateId: string) => {
    setExerciseTemplateToDelete(exerciseTemplateId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalSubmit = async () => {
    if (!workoutTemplate) return;

    setIsDeleteModalOpen(false);

    const newWorkoutTemplateExercises = [...workoutTemplate.exercises].filter(
      (exercise) => {
        return exercise._id !== exerciseTemplateToDelete;
      },
    );

    setExerciseTemplateToDelete(null);

    setWorkoutTemplate((prevWorkoutTemplate) => {
      if (prevWorkoutTemplate) {
        return {
          ...prevWorkoutTemplate,
          exercises: prevWorkoutTemplate.exercises.filter((exercise) => {
            return exercise._id !== exerciseTemplateToDelete;
          }),
        };
      }
      return prevWorkoutTemplate;
    });

    try {
      const removeExerciseResponse = await axiosClient.put(
        `/api/workout-templates/${workoutTemplateId}`,
        { exercises: newWorkoutTemplateExercises },
      );

      if (removeExerciseResponse.status !== 200) {
        throw new Error(removeExerciseResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Deleting Exercise Sequence",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(true);
      getWorkoutTemplate();
    }
  };

  const handleNotesUpdated = async (updatedNotes: string) => {
    if (!workoutTemplate) return;

    if (updatedNotes !== workoutTemplate.notes) {
      try {
        const updateWorkoutTemplateNotesResponse = await axiosClient.put(
          `/api/workout-templates/${workoutTemplateId}`,
          { notes: updatedNotes },
        );

        if (updateWorkoutTemplateNotesResponse.status !== 200) {
          throw new Error(updateWorkoutTemplateNotesResponse.data.message);
        }
      } catch (e: any) {
        console.error(e);
        const errorMessage =
          e?.response?.data?.error || "Something unexpected occured.";
        setModalState({
          isOpen: true,
          type: "error",
          title: "Error Saving Program Workout Notes",
          message: `${errorMessage} Please try again or contact support.`,
        });
        setIsInitialLoading(true);
        getWorkoutTemplate();
      }
    }
  };

  const handleExerciseDragAndDrop = async (results: DropResult) => {
    const { source, destination } = results;

    if (!workoutTemplate) return;
    if (!destination) return;
    if (source.index === destination.index) return;

    const reorderedExercises = [...workoutTemplate.exercises];
    const [movedExercise] = reorderedExercises.splice(source.index, 1);
    reorderedExercises.splice(destination.index, 0, movedExercise);

    setWorkoutTemplate((prevWorkoutTemplate) => {
      if (prevWorkoutTemplate) {
        return {
          ...prevWorkoutTemplate,
          exercises: reorderedExercises,
        };
      }
      return prevWorkoutTemplate;
    });

    try {
      const updateWorkoutTemplateResponse = await axiosClient.put(
        `/api/workout-templates/${workoutTemplateId}`,
        { exercises: reorderedExercises },
      );

      if (updateWorkoutTemplateResponse.status !== 200) {
        throw new Error(updateWorkoutTemplateResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Moving Exercise",
        message: `${errorMessage} Please try again or contact support.`,
      });

      setIsInitialLoading(true);
      getWorkoutTemplate();
    }
  };

  return (
    <div className="h-full relative">
      <PreviousPageHeader
        previousPageLink={`/programs/${programId}`}
        pageTitle="Program Workout"
      />
      <div className="h-[calc(100%-63px)] overflow-auto">
        <p className="p-2 pb-0 text-xs font-regular text-gray-500">
          Name your workout and add exercises to your plan. These exercises will
          be pre-populated into your workout log.
        </p>
        <div className="pt-2 px-2">
          <BasicInput
            id="workout-template-name"
            label="Name"
            placeholder="Leg Day, Upper Body Day, etc."
            initialValue={
              workoutTemplate?.name === "Untitled Program Workout"
                ? ""
                : workoutTemplate?.name
            }
            onBlur={handleTemplateNameSubmit}
            isLoading={isInitialLoading}
          />
        </div>
        <h2 className="pt-4 pb-1 px-2 block text-sm font-medium text-gray-700">
          Exercises
        </h2>
        {!isInitialLoading ? (
          <div className="px-2 pr-0 flex flex-col gap-y-1">
            {workoutTemplate && workoutTemplate.exercises.length === 0 && (
              <EmptyListState
                title="No template exercises"
                message="Add a new exercise to get started."
                fullscreenList
              />
            )}
            {workoutTemplate && workoutTemplate.exercises.length > 0 && (
              <DragDropContext onDragEnd={handleExerciseDragAndDrop}>
                <Droppable droppableId="ROOT" type="group">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {workoutTemplate.exercises.map((exercise, index) => {
                        return (
                          <Draggable
                            draggableId={exercise._id}
                            key={exercise._id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                ref={provided.innerRef}
                                className="mb-1"
                              >
                                <ExerciseTemplateCard
                                  key={exercise._id}
                                  {...exercise}
                                  className="mr-2"
                                  onDeleteClicked={
                                    handleInitiateDeleteExerciseTemplate
                                  }
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        ) : (
          <div className="px-2 pr-0 flex flex-col gap-y-1">
            {Array.from({ length: amountOfCardSkeletons }, (number, index) => (
              <TextSkeleton key={index} height="58px" className="w-full" />
            ))}
          </div>
        )}

        <div className="py-4 px-2 pt-2 w-full bg-gray-50">
          <FullWidthAsyncButton
            text="New Exercise"
            loadingText="Creating Exercise Sequence"
            icon="PlusIcon"
            onClick={handleAddNewExercise}
            isSkeleton={isInitialLoading}
          />
        </div>
        <div>
          <NotesInput
            placeholder=""
            notes={workoutTemplate?.notes}
            onNotesUpdated={handleNotesUpdated}
            needOnFocusEvent
            isLoading={isInitialLoading}
          />
        </div>
      </div>
      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onModalClose={() => {
          setIsDeleteModalOpen(false);
          setExerciseTemplateToDelete(null);
        }}
        onSubmit={handleDeleteModalSubmit}
        deleteTitle={"Delete Exercise Sequence"}
        deleteMessage={
          "Are you sure you want to delete?\nThis Exercise Sequence and all its set data will be permanently removed.\nThis action cannot be undone."
        }
      />
    </div>
  );
};

export default WorkoutTemplate;
